import React, { JSX, useCallback, useRef, useState } from 'react';
import SingleSelection from '../../Components/SingleSelection';
import { isEqual } from 'lodash-es';
import { PackageDropdownFilter, PackageSearchParams } from '@ibe/components';
import { PackageCode, ProductIBEProps } from './PackagesList';
import { ApiBaseData, ApiBestPricePackageModel, ApiGeoUnit, ApiRentalCarOffice } from '@ibe/api';
import dayjs from 'dayjs';
import { PromotionCodeData } from '../../Components/PromotionCode';
import { MINI_GROUP, SMALL_GROUP } from '../../Util/constant';

export type FilterParams = {
  numberOfTravelers: number;
  origins: string[];
  busStations: string[];
  startDate: string;
  endDate: string;
  groupSize?: string;
  selectedPostalData: ApiBaseData | undefined;
};

export type InitialStorageFilters = {
  initialFilters?: FilterParams;
  initialFiltersChecked: boolean;
};

const filterPropsMapper = (
  groupSize?: string,
  searchParams?: Partial<PackageSearchParams>
): FilterParams => {
  return {
    numberOfTravelers: !!searchParams?.occupancy ? searchParams.occupancy[0].adults : 0,
    origins: searchParams?.origins?.map((origin: ApiGeoUnit) => origin.id) || [],
    busStations: searchParams?.busStations?.map((office: ApiRentalCarOffice) => office.code) || [],
    startDate: searchParams?.startDate || '',
    endDate: searchParams?.endDate || '',
    groupSize
  } as FilterParams;
};

const Filters = ({
  earliestPossibleDate,
  latestPossibleDate,
  onChange,
  origins,
  packageCodes,
  maintenanceMode,
  numberOfTravelers,
  packages = [],
  promotionCode
}: {
  earliestPossibleDate: string;
  latestPossibleDate: string;
  onChange: (filters: FilterParams, ignoreNumberOfTravelers?: boolean) => void;
  origins: string[];
  packageCodes: ProductIBEProps['packageCodes'];
  maintenanceMode?: boolean;
  numberOfTravelers: number;
  packages: ApiBestPricePackageModel[];
  promotionCode: PromotionCodeData;
}): JSX.Element => {
  type Mode = 'selectsStart' | 'selectsEnd' | 'none';
  const dateRangeRef = useRef<{
    focus?: (a: Mode) => void;
  }>({
    focus: () => {}
  });

  const [groupSize, setGroupSize] = useState<string | undefined>(undefined);

  const [packageSearchParams, setPackageSearchParams] = useState<PackageSearchParams | undefined>(
    undefined
  );

  const handleFilterChange = useCallback(
    (searchParams: Partial<PackageSearchParams>): void => {
      if (!isEqual(searchParams, packageSearchParams)) {
        setPackageSearchParams(searchParams);
        onChange(filterPropsMapper(groupSize, searchParams));
      }
    },
    [onChange, promotionCode]
  );

  const handleGroupSizeChecked = useCallback(
    (grpSize: string): void => {
      grpSize = grpSize === groupSize ? '' : grpSize;
      setGroupSize(grpSize);
      onChange(filterPropsMapper(grpSize, packageSearchParams), true);
    },
    [onChange, promotionCode]
  );

  const onToggle = useCallback(
    ({ isOpen, endDate, startDate }: { isOpen?: boolean; endDate?: Date; startDate?: Date }) => {
      if (isOpen && !endDate && startDate && dateRangeRef && dateRangeRef.current?.focus) {
        dateRangeRef.current?.focus('selectsEnd');
      }
    },
    [dateRangeRef]
  );

  const checkForGroupSize = (grpSize: string) => {
    return (
      packageCodes.some(code => code.groupSize === grpSize) &&
      !packageCodes.every(code => code.groupSize === grpSize) &&
      (packageCodes.reduce(
        (total: string[], current: PackageCode) =>
          !total.includes(current.code) ? [...total, current.code] : [...total],
        []
      ).length > 1 ||
        !packageCodes.some(code => code.groupSize === grpSize)) &&
      packages.some(singlePackage => singlePackage.groupSize === grpSize)
    );
  };

  return (
    <>
      {(checkForGroupSize(SMALL_GROUP) ||
        packageCodes.some(code => code.groupSize === SMALL_GROUP)) &&
      !maintenanceMode ? (
        <SingleSelection
          selectionId={'small-group-selection'}
          groupSizeChecked={groupSize === SMALL_GROUP}
          setIsChecked={() => handleGroupSizeChecked(SMALL_GROUP)}
          groupSize={SMALL_GROUP}
          promotionCode={promotionCode}
          showInfoTextInFront={true}
        />
      ) : (
        <></>
      )}
      {(checkForGroupSize(MINI_GROUP) ||
        packageCodes.some(code => code.groupSize === MINI_GROUP)) &&
      !maintenanceMode ? (
        <SingleSelection
          selectionId={'mini-group-selection'}
          groupSizeChecked={groupSize === MINI_GROUP}
          setIsChecked={() => handleGroupSizeChecked(MINI_GROUP)}
          groupSize={MINI_GROUP}
          promotionCode={promotionCode}
          showInfoTextInFront={true}
        />
      ) : (
        <></>
      )}
      <PackageDropdownFilter
        onChange={handleFilterChange}
        datepickerMaxDate={latestPossibleDate}
        datepickerMinDate={earliestPossibleDate}
        origins={origins}
        disabled={maintenanceMode}
        defaultDate={{
          startDate: dayjs(earliestPossibleDate).isBefore(dayjs(new Date()))
            ? `${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`
            : earliestPossibleDate,
          endDate: latestPossibleDate
        }}
        onToggle={{
          calendar: onToggle
        }}
        dateRangeRef={dateRangeRef}
        numberOfTravelers={numberOfTravelers}
        memoDeps={[promotionCode]}
        showNumberSelector
      />
    </>
  );
};

export default Filters;
